import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { LucideIcon } from 'lucide-react';
import {
  Brain,
  Check,
  Cloud,
  Hexagon,
  Layers,
  Search,
  Smile,
  Sparkles,
  User,
  Wind,
  X,
} from 'lucide-react';
import PillTabs from '../../../../components/common/PillTabs';
import { logLlmConfig } from './llmConfigLogger';

export interface LLMProviderType {
  id: string;
  name: string;
  providerType: number;
  description?: string;
}

export const PROVIDER_TYPE_GENERATION = 1;
export const PROVIDER_TYPE_EMBEDDING = 2;

type ProviderFilter = 'all' | 'generation' | 'embedding';

interface ProviderVisual {
  Icon: LucideIcon | null;
  initials: string;
  tileClass: string;
}

const FALLBACK_TILES = [
  'from-indigo-500 to-violet-600',
  'from-cyan-500 to-blue-600',
  'from-fuchsia-500 to-pink-600',
  'from-lime-500 to-emerald-600',
  'from-amber-500 to-orange-600',
  'from-rose-500 to-red-600',
  'from-sky-500 to-indigo-600',
];

const providerInitials = (name: string): string => {
  const words = name.replace(/[()]/g, ' ').split(/\s+/).filter(Boolean);
  if (words.length === 0) return 'AI';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return `${words[0][0]}${words[1][0]}`.toUpperCase();
};

const hashIndex = (value: string, modulo: number): number => {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 31 + value.charCodeAt(i)) >>> 0;
  }
  return hash % modulo;
};

export const resolveProviderVisual = (name: string, providerType: number): ProviderVisual => {
  const n = name.toLowerCase();
  const isEmbedding = providerType === PROVIDER_TYPE_EMBEDDING;
  const initials = providerInitials(name);

  if (n.includes('openai')) {
    return {
      Icon: isEmbedding ? Layers : Sparkles,
      initials,
      tileClass: isEmbedding ? 'from-emerald-600 to-teal-700' : 'from-emerald-400 to-green-600',
    };
  }
  if (n.includes('anthropic') || n.includes('claude')) {
    return {
      Icon: Brain,
      initials,
      tileClass: 'from-orange-400 to-amber-600',
    };
  }
  if (n.includes('azure')) {
    return {
      Icon: isEmbedding ? Layers : Cloud,
      initials,
      tileClass: isEmbedding ? 'from-sky-600 to-blue-800' : 'from-sky-400 to-blue-600',
    };
  }
  if (n.includes('cohere')) {
    return {
      Icon: isEmbedding ? Layers : Sparkles,
      initials,
      tileClass: isEmbedding ? 'from-violet-600 to-purple-800' : 'from-violet-400 to-fuchsia-600',
    };
  }
  if (n.includes('google') || n.includes('gemini')) {
    return {
      Icon: isEmbedding ? Layers : Hexagon,
      initials,
      tileClass: isEmbedding ? 'from-blue-500 to-red-500' : 'from-blue-500 via-amber-400 to-red-500',
    };
  }
  if (n.includes('huggingface') || n.includes('hugging face')) {
    return {
      Icon: Smile,
      initials,
      tileClass: 'from-yellow-400 to-amber-600',
    };
  }
  if (n.includes('humain')) {
    return {
      Icon: User,
      initials,
      tileClass: 'from-rose-400 to-pink-600',
    };
  }
  if (n.includes('mistral')) {
    return {
      Icon: Wind,
      initials,
      tileClass: 'from-orange-500 to-red-600',
    };
  }

  return {
    Icon: null,
    initials,
    tileClass: FALLBACK_TILES[hashIndex(name, FALLBACK_TILES.length)],
  };
};

export const getProviderKindLabel = (providerType: number): string => {
  if (providerType === PROVIDER_TYPE_GENERATION) return 'Generation';
  if (providerType === PROVIDER_TYPE_EMBEDDING) return 'Embeddings';
  return 'Provider';
};

const ProviderIconTile = ({
  visual,
  size = 'md',
}: {
  visual: ProviderVisual;
  size?: 'sm' | 'md';
}) => {
  const box = size === 'sm' ? 'w-9 h-9 rounded-lg' : 'w-11 h-11 rounded-xl';
  const iconSize = size === 'sm' ? 'w-4 h-4' : 'w-5 h-5';
  return (
    <div
      className={`${box} bg-gradient-to-br ${visual.tileClass} flex items-center justify-center flex-shrink-0 shadow-md shadow-black/20`}
    >
      {visual.Icon ? (
        <visual.Icon className={`${iconSize} text-white`} strokeWidth={1.75} />
      ) : (
        <span className="text-white text-xs font-semibold tracking-wide">{visual.initials}</span>
      )}
    </div>
  );
};

interface LlmProviderFieldProps {
  providers: LLMProviderType[];
  selectedId: string;
  onSelect: (providerId: string) => void;
  loading?: boolean;
}

export const LlmProviderField: React.FC<LlmProviderFieldProps> = ({
  providers,
  selectedId,
  onSelect,
  loading = false,
}) => {
  const [open, setOpen] = useState(false);
  const selected = providers.find((p) => p.id === selectedId);
  const visual = selected ? resolveProviderVisual(selected.name, selected.providerType) : null;

  const openPicker = () => {
    if (loading) return;
    logLlmConfig('app', 'provider_picker_opened', { selectedId: selectedId || null, count: providers.length });
    setOpen(true);
  };

  return (
    <>
      <button
        type="button"
        onClick={openPicker}
        disabled={loading}
        aria-label={selected ? `Change provider, currently ${selected.name}` : 'Search providers'}
        className="w-full min-h-[42px] bg-neutral-800 px-3 py-2 rounded text-left outline-none disabled:opacity-60 disabled:cursor-not-allowed border border-transparent hover:border-neutral-700 focus-visible:border-indigo-500/60 transition-colors"
      >
        {loading ? (
          <span className="text-gray-400">Loading providers…</span>
        ) : selected && visual ? (
          <div className="flex items-center gap-3">
            <ProviderIconTile visual={visual} size="sm" />
            <div className="min-w-0 flex-1">
              <p className="text-white truncate">{selected.name}</p>
              {selected.description ? (
                <p className="text-[11px] text-gray-400 truncate">{selected.description}</p>
              ) : null}
            </div>
            <span className="inline-flex items-center gap-1 text-xs text-indigo-300 flex-shrink-0">
              <Search className="w-3.5 h-3.5" />
              Search
            </span>
          </div>
        ) : (
          <span className="inline-flex items-center gap-2 text-gray-400">
            <Search className="w-4 h-4" />
            Search providers
          </span>
        )}
      </button>
      {open && (
        <LlmProviderPicker
          providers={providers}
          selectedId={selectedId}
          onSelect={(id) => {
            onSelect(id);
            setOpen(false);
          }}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
};

interface LlmProviderPickerProps {
  providers: LLMProviderType[];
  selectedId: string;
  onSelect: (providerId: string) => void;
  onClose: () => void;
}

const LlmProviderPicker: React.FC<LlmProviderPickerProps> = ({
  providers,
  selectedId,
  onSelect,
  onClose,
}) => {
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<ProviderFilter>('all');
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    searchRef.current?.focus();
  }, []);

  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const counts = useMemo(() => ({
    all: providers.length,
    generation: providers.filter((p) => p.providerType === PROVIDER_TYPE_GENERATION).length,
    embedding: providers.filter((p) => p.providerType === PROVIDER_TYPE_EMBEDDING).length,
  }), [providers]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return providers.filter((p) => {
      if (filter === 'generation' && p.providerType !== PROVIDER_TYPE_GENERATION) return false;
      if (filter === 'embedding' && p.providerType !== PROVIDER_TYPE_EMBEDDING) return false;
      if (!q) return true;
      return p.name.toLowerCase().includes(q) || (p.description ?? '').toLowerCase().includes(q);
    });
  }, [providers, query, filter]);

  const handleSelect = (provider: LLMProviderType) => {
    logLlmConfig('app', 'provider_selected', {
      providerId: provider.id,
      name: provider.name,
      providerType: provider.providerType,
    });
    onSelect(provider.id);
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[120] bg-black/70 backdrop-blur-sm flex justify-center items-center p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="llm-provider-picker-title"
        className="bg-neutral-900 w-full max-w-4xl rounded-2xl shadow-2xl border border-neutral-800 flex flex-col max-h-[86vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex-shrink-0 px-6 pt-5 pb-4 border-b border-neutral-800 bg-gradient-to-b from-indigo-500/10 to-transparent">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 id="llm-provider-picker-title" className="text-white text-lg font-semibold">
                Choose a provider
              </h2>
              <p className="text-sm text-gray-400 mt-1">
                Search generation and embedding providers for this configuration.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-white p-1 rounded-md hover:bg-neutral-800"
              aria-label="Close provider catalog"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="relative mt-4">
            <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              ref={searchRef}
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name or description"
              aria-label="Search providers"
              className="w-full bg-neutral-800 border border-neutral-700 focus:border-indigo-500/60 rounded-lg pl-9 pr-3 py-2.5 text-sm text-white outline-none placeholder:text-gray-500"
            />
          </div>

          <div className="mt-3">
            <PillTabs
              isLight={false}
              ariaLabel="Filter providers by type"
              value={filter}
              size="sm"
              onChange={setFilter}
              items={[
                { id: 'all', label: 'All', count: counts.all },
                { id: 'generation', label: 'Generation', count: counts.generation },
                { id: 'embedding', label: 'Embeddings', count: counts.embedding },
              ]}
            />
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto p-6 bg-neutral-950">
          {visible.length === 0 ? (
            <div className="h-48 flex flex-col items-center justify-center text-center">
              <div className="w-12 h-12 rounded-xl bg-neutral-800 flex items-center justify-center mb-3">
                <Search className="w-5 h-5 text-gray-500" />
              </div>
              <p className="text-white text-sm font-medium">No providers match</p>
              <p className="text-gray-500 text-xs mt-1">Try another search or filter.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {visible.map((provider) => {
                const visual = resolveProviderVisual(provider.name, provider.providerType);
                const selected = provider.id === selectedId;
                const kind = getProviderKindLabel(provider.providerType);
                return (
                  <button
                    key={provider.id}
                    type="button"
                    onClick={() => handleSelect(provider)}
                    className={`group relative text-left overflow-hidden rounded-xl border p-3.5 bg-neutral-800 transition-all duration-150 ${
                      selected
                        ? 'border-indigo-400 ring-1 ring-indigo-400/40'
                        : 'border-neutral-700 hover:border-neutral-500'
                    }`}
                  >
                    <span className={`absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r ${visual.tileClass}`} />
                    <div className="flex items-start gap-3">
                      <ProviderIconTile visual={visual} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <span
                            className={`text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded-full ${
                              provider.providerType === PROVIDER_TYPE_EMBEDDING
                                ? 'bg-teal-500/15 text-teal-300'
                                : 'bg-indigo-500/15 text-indigo-300'
                            }`}
                          >
                            {kind}
                          </span>
                          {selected ? <Check className="w-4 h-4 text-indigo-400 flex-shrink-0" /> : null}
                        </div>
                        <p className="text-sm text-white font-medium mt-1.5 truncate">{provider.name}</p>
                        <p className="text-xs text-gray-400 mt-0.5 line-clamp-2 min-h-[2rem]">
                          {provider.description || 'No description'}
                        </p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex-shrink-0 flex items-center justify-between px-6 py-3.5 border-t border-neutral-800 bg-neutral-950">
          <p className="text-xs text-gray-500 tabular-nums">
            {visible.length} of {providers.length} providers
          </p>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-white text-sm">
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default LlmProviderPicker;
